import { 
  createIntFormatter, 
  createPercentFormatter,
  textFormatter
} from "../../../../../utils/formatter/formatter";

import { i18n } from '../../../../../translate/i18n'

const { t } = i18n;

const titlesAndLabels = {
  title: {
    cardIcons: {
      reference: textFormatter(t("reference"), { textTransform: 'uppercase' }),
      brand: textFormatter(t("brand"), { textTransform: 'uppercase' }),
      collection: textFormatter(t("collection"), { textTransform: 'uppercase' }),
      segment: textFormatter(t("segment"), { textTransform: 'uppercase' }),
      regional: textFormatter(t("regional"), { textTransform: 'uppercase' }),
      salesRepresentative: textFormatter(t("salesRepresentative"), { textTransform: 'uppercase' }),
    },
    details: {
      ranking: textFormatter(t("ranking", { textTransform: 'uppercase' })),
      performance: textFormatter(t("performance", { textTransform: 'uppercase' })),
      salesByColor: textFormatter(t("salesByColor", { textTransform: 'uppercase' })),
      salesBySizeGrid: textFormatter(t("salesBySizeGrid", { textTransform: 'uppercase' })),            
    }
  },
  label: {
    details: {
      unitsSold: textFormatter(t("unitsSold", { textTransform: 'uppercase' })),
      revenue: textFormatter(t("revenue", { textTransform: 'uppercase' })),
      customerAdoption: textFormatter(t("customerAdoption", { textTransform: 'uppercase' })),
      customerAdoptionClip: textFormatter(t("customerAdoption"), { clipLength: 12, textTransform: 'clip' }),
      wholesale: textFormatter(t("wholesale", { textTransform: 'uppercase' })),
      retail: textFormatter(t("retail", { textTransform: 'uppercase' })),
      avgPrice: textFormatter(t("avgPrice", { textTransform: 'uppercase' })),
      totalSales: textFormatter(t("totalSales", { textTransform: 'uppercase' })),
    }
  }
}

const parsePipeList = (raw) => {
  if (!raw) return [];
  return raw
    .split('|')
    .map(entry => {
      const [label, pct] = entry.split(':').map(s => s?.trim());
      return { label, pct: Number(pct) };
    })
    .filter(item => item.label && !Number.isNaN(item.pct));
};

const COLOR_PALETTE = [
  '#d4a853', 
  '#7b9ecf', 
  '#5a8a5c', 
  '#e8773a', 
  '#6b7280', 
  '#1d4ed8', 
  '#be185d', 
  '#065f46'
];
const SIZE_PALETTE = ['#E8773A', '#9ca3af', '#c4c4c4', '#e0e0e0'];

const intFmt = createIntFormatter()
const percentFmt = createPercentFormatter();
const currencyFormatterWithCents = new Intl.NumberFormat("pt-BR", {
  style: "currency",
  currency: "BRL",
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

export const ProductDetailPanel = ({ ctx, uiState }) => {

  const { product, total } = ctx || {};
  const { productTagData, isLoadingProductTag } = uiState || {};

  const { financialData } = product || {};
  const { buyers } = product || {};
  const { rankPositions } = product || {};
  const { salesByProductFeatures } = product || {};
  const { levels } = product || {}

  const { salesByProductColor, salesByProductSize } = salesByProductFeatures || {};
  const { revenue, averagePrice, unitsSold, netShare } = financialData || {};
  const { regional, representative } = buyers || {};
  const { shares } = buyers || {};
  const { buyingCustomersShare, retailCustomersShare, wholesaleCustomersShare } = shares || {};
  const { purchaseAffinity, totalSales, revenue: revenueRankPosition } = rankPositions || {};
  const { level1, level2, level3, level4 } = levels || {}

  const breadcrumb = [level1, level2, level3, level4].join(' · ');

  const colorData = parsePipeList(salesByProductColor).map((item, i) => ({
    ...item,
    label: textFormatter(item.label),
    hex: COLOR_PALETTE[i % COLOR_PALETTE.length],
  }));

  const sizeData = parsePipeList(salesByProductSize);

  const infoCards = [
    { 
      label: titlesAndLabels?.title?.cardIcons?.reference, 
      value: product.name ?? "-",
      hasLargerFontSize: true,
      icon: (
        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M20.59 13.41l-7.17 7.17a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z"/>
          <line x1="7" y1="7" x2="7.01" y2="7"/>
      </svg>
      )
    },
    { 
      label: titlesAndLabels?.title?.cardIcons?.brand, 
      value: product.levels?.level1 || '-',
      hasLargerFontSize: true,
      icon: (
        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M6 9H4.5a2.5 2.5 0 0 1 0-5H6"/>
          <path d="M18 9h1.5a2.5 2.5 0 0 0 0-5H18"/>
          <path d="M4 22h16"/>
          <path d="M10 14.66V17c0 .55-.47.98-.97 1.21C7.85 18.75 7 20.24 7 22"/>
          <path d="M14 14.66V17c0 .55.47.98.97 1.21C16.15 18.75 17 20.24 17 22"/>
          <path d="M18 2H6v7a6 6 0 0 0 12 0V2z"/>
        </svg>        
      ),
    },
    { 
      label: titlesAndLabels?.title?.cardIcons?.collection, 
      value: product?.season?.seasonName || '-',
      hasLargerFontSize: false,
      icon: (
        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M20.59 13.41l-7.17 7.17a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z"/>
          <line x1="7" y1="7" x2="7.01" y2="7"/>
        </svg>         
      )
    },
    { 
      label: titlesAndLabels?.title?.cardIcons?.segment, 
      value: product?.levels?.level3 || '-',
      hasLargerFontSize: false,
      icon: (
        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
          <circle cx="9" cy="7" r="4"/>
          <path d="M23 21v-2a4 4 0 0 0-3-3.87"/>
          <path d="M16 3.13a4 4 0 0 1 0 7.75"/>
        </svg>        
      )     
    },
    { 
      label: titlesAndLabels?.title?.cardIcons?.regional, 
      value: textFormatter(regional) || "-",
      hasLargerFontSize: false,
      icon: (
        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/>
          <circle cx="12" cy="10" r="3"/>
        </svg>           
      )
    },
    { 
      label: titlesAndLabels?.title?.cardIcons?.salesRepresentative, 
      value: textFormatter(representative) || "-",
      hasLargerFontSize: false,
      icon: (
        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/>
          <circle cx="12" cy="7" r="4"/>
        </svg>   
      )
    },
  ];

  const rankingInfo = [
    {
      label: titlesAndLabels?.label?.details?.unitsSold,
      value: totalSales,
      icon: (
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"/>
          <polyline points="3.27 6.96 12 12.01 20.73 6.96"/>
          <line x1="12" y1="22.08" x2="12" y2="12"/>
        </svg>
      ),
    },
    {
      label: titlesAndLabels?.label?.details?.revenue,
      value: intFmt.format(revenueRankPosition),
      icon: (
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <line x1="12" y1="1" x2="12" y2="23"/>
          <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"/>
        </svg>
      ),
    },
    {
      label: titlesAndLabels?.label?.details?.customerAdoption,
      value: purchaseAffinity != null ? intFmt.format(purchaseAffinity) : '—',
      icon: (
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
          <circle cx="9" cy="7" r="4"/>
          <path d="M23 21v-2a4 4 0 0 0-3-3.87"/>
          <path d="M16 3.13a4 4 0 0 1 0 7.75"/>
        </svg>
      ),
    },
  ];

  const performanceInfo = [
    {
      label: titlesAndLabels?.label?.details?.totalSales,
      value: unitsSold != null ? intFmt.format(unitsSold) : '—',
      highlightBackground: true,
      highlightBorder: true,
      highlightIcon: false,
      highlightLabel: false,
      highlightValue: true,
      icon: (
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"/>
          <polyline points="3.27 6.96 12 12.01 20.73 6.96"/>
          <line x1="12" y1="22.08" x2="12" y2="12"/>
        </svg>
      ),
    },
    {
      label: titlesAndLabels?.label?.details?.wholesale,
      highlightBackground: false,
      highlightBorder: false,
      highlightIcon: false,
      highlightLabel: false,
      highlightValue: false,      
      value: wholesaleCustomersShare != null ? intFmt.format(wholesaleCustomersShare) : '—',
    },
    {
      label: titlesAndLabels?.label?.details?.retail,
      highlightBackground: false,
      highlightBorder: false,
      highlightIcon: false,
      highlightLabel: false,
      highlightValue: false,         
      value: retailCustomersShare != null ? intFmt.format(retailCustomersShare) : '—',
    },
    {
      label: titlesAndLabels?.label?.details?.customerAdoptionClip,
      highlightBackground: false,
      highlightBorder: false,
      highlightIcon: false,
      highlightLabel: false,
      highlightValue: false,         
      value: buyingCustomersShare != null ? percentFmt.format(buyingCustomersShare) : '—',
      icon: (
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
          <circle cx="9" cy="7" r="4"/>
          <path d="M23 21v-2a4 4 0 0 0-3-3.87"/>
          <path d="M16 3.13a4 4 0 0 1 0 7.75"/>
        </svg>
      ),
    },
    {
      label: titlesAndLabels?.label?.details?.avgPrice,
      highlightBackground: false,
      highlightBorder: false,
      highlightIcon: false,
      highlightLabel: false,
      highlightValue: false,         
      value: averagePrice != null ? currencyFormatterWithCents.format(averagePrice) : '—',
    },
    {
      label: titlesAndLabels?.label?.details?.revenue,
      highlightBackground: true,
      highlightBorder: true,
      highlightIcon: false,
      highlightLabel: false,
      highlightValue: true,      
      value: revenue != null ? currencyFormatterWithCents.format(revenue) : '—',
      icon: (
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <line x1="12" y1="1" x2="12" y2="23"/>
          <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"/>
        </svg>
      ),
    },
  ];

  return (
    <div className="parent-grid">
      {/* ── COLUNA ESQUERDA ── */}
      <section className="info-img-background">
        <div style={{ display: 'grid', gridTemplateColumns: '1fr auto' }}>
          {/* Informações do card */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
              {infoCards.map(card => (
                <div key={card?.label} style={{
                  backgroundColor: '#fff', border: '1px solid #f0f0f0',
                  borderRadius: '8px', padding: '0.375rem 0.5rem',
                  display: 'flex', alignItems: 'center', gap: 10,
                }}>
                  <span style={{
                    width: 28, height: 28, borderRadius: '8px', border: '1px solid #fde8d5',
                    backgroundColor: '#fff6f0', display: 'inline-flex', alignItems: 'center',
                    justifyContent: 'center', flexShrink: 0, color: '#E8773A',
                  }}>
                    {card?.icon}
                  </span>
                  
                  <hgroup>
                    <p style={{ fontSize: '0.563rem', fontWeight: '700', color: '#bbb', lineHeight: '0.4rem' }}>
                      {card.label}
                    </p>
                    <p style={{ 
                      fontSize: card?.hasLargerFontSize ? '1rem' : '0.875rem',
                      fontWeight: '600', color: '#333333' }}>
                      {card?.value}
                    </p>
                  </hgroup>
                </div>
              ))}
            </div>

            {/* Imagem */}
            <div style={{ 
              backgroundColor: 'rgba(240,240,240,0.3)', backgroundImage: 'radial-gradient(circle, rgba(234, 88, 12, 0.05), rgba(0, 0, 0, 0) 60%)',
              border: '1px solid rgba(230,230,230)', 
              borderRadius: '8px', display: 'flex', flexDirection: 'column' }}>

              <div style={{
                padding: '8px 12px', backgroundColor: 'rgba(240, 240, 240, 0.3)', 
                borderBottom: '1px solid rgba(230,230,230)', boxSizing: 'border-box',
                display: 'flex', alignItems: 'center'
              }}>
                <span style={{
                  width: "fit-content", height: "fit-content", borderRadius: '6px', border: '1px solid #fde8d5',
                  backgroundColor: '#fff6f0', display: 'inline-flex', alignItems: 'center',
                  justifyContent: 'center', flexShrink: 0, color: '#E8773A', padding: '0.375rem'
                }}>
                  <svg width="20" height="16" viewBox="0 0 24 24" fill="none" stroke="#E8773A" strokeWidth="2">
                    <path d="M20.59 13.41l-7.17 7.17a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z"/>
                    <line x1="7" y1="7" x2="7.01" y2="7"/>
                  </svg>
                </span>
                <p style={{ fontSize: '0.625rem', fontWeight: '700', color: '#555', }}>
                  IMAGEM TAG
                </p>
              </div>

              {/* Exibição da imagem */}
              <div style={{ 
                padding: '1rem', display: 'flex', alignItems: 'center', 
                justifyContent: 'center', backgroundColor: 'rgba(252, 252, 252)',
                borderBottomLeftRadius: '8px', borderBottomRightRadius: '8px' }}>
                {isLoadingProductTag ? (
                  <div style={{
                    backgroundColor: '#f8f8f8', borderRadius: '6px',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                  }}>
                    <span style={{ color: '#bbb', fontSize: '11px' }}>Carregando...</span>
                  </div>
               ) : productTagData?.tag ? (
                  <img
                    src={`data:${productTagData.mimeType};base64,${productTagData.tag}`}
                    alt="produto"
                    width={168}
                    height={168}
                    style={{ height: '100%', width: '100%', objectFit: 'contain', borderRadius: '4px', border: '1px solid #f0f0f0' }}
                  />
                ) : (
                  <figure style={{
                    width: '100%', height: '400px', backgroundColor: '#f8f8f8', borderRadius: '6px', padding: '1rem',
                    display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 6,
                    margin: 0, overflow: 'hidden', boxShadow: '0px 4px 8px 0px rgba(0, 0, 0, 0.1)'
                  }}>
                    <img 
                      alt="tags"
                      src="/img/sem_TAG.png"
                      style={{ width: '100%', height: '100%', maxInlineSize: "100%", objectFit: 'contain' }}
                    />
                  </figure>
                )}
             </div>
            </div>

            {/* ── BREADCRUMB ──*/}
            <p style={{ 
              padding: '6px 20px 12px', borderTop: '1px solid #f0f0f0', 
              fontSize: '0.625rem', color: '#bbb', margin: 0, fontWeight: 700 }}>
              {breadcrumb}
            </p>
          </div>
        </div>
      </section>

      {/* ── DIVISOR CENTRAL ── */}
      <div className="divider-info-product-detail" />
      <div className="divider-info-product-detail-svg" style={{
        inlineSize: 28, height: 28, borderRadius: '50%', backgroundColor: '#E8773A',
        display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
      }}>
        <svg inlineSize="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.5">
          <polyline points="23 6 13.5 15.5 8.5 10.5 1 18"/>
          <polyline points="17 6 23 6 23 12"/>
        </svg>
      </div>

      {/* ── COLUNA DIREITA ── */}
      <section className="product-detail-background">
        <div style={{ display: 'flex', flexDirection: 'column', gap: '1.6rem' }}>

          {/* Ranking */}
          <section style={{ 
            border: '1px solid #f0f0f0', borderRadius: '8px', margin: 0,
            backgroundColor: 'rgba(255, 255, 255)', height: 'fit-content' }}>
            <div style={{ 
              backgroundColor: "rgba(240, 240, 240, 0.9)", borderBottom: '1px solid #ebebeb', 
              display: 'flex', alignItems: 'center', gap: 8, paddingBlock: '0.4rem' }}>

              <span style={{ 
                width: 3, height: 18, backgroundColor: '#E8773A', 
                borderRadius: 3, flexShrink: 0, display: 'inline-block' }} />
              <p style={{ fontSize: '0.625rem', fontWeight: '700', color: '#333333' }}>
                RANKING
              </p>
            </div>

            <section style={{ 
              display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', 
              gap: 8, padding: '10px 14px', backgroundColor: "#fff" }}>
              {rankingInfo.map(item => (
                <div
                  key={item.label}
                  style={{ 
                    backgroundColor: '#f0f0f04d', border: '1px solid #efefef', 
                    borderRadius: '8px', padding: '8px 10px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 4, marginBottom: 6, color: '#aaa' }}>
                    {item.icon}
                    <span style={{ 
                      fontSize: '0.563rem', fontWeight: '700', 
                      color: '#aaa', letterSpacing: '0.04em' }}>
                      {item.label}
                    </span>
                  </div>

                  <hgroup style={{ display: 'flex', alignItems: 'baseline', gap: 3 }}>
                    <span style={{ fontSize: '1.6rem', fontWeight: '700', color: '#333333', lineHeight: 1 }}>
                      {item.value}
                    </span>

                    {item.value != null && (
                      <span style={{ 
                        fontSize: '0.8rem', color: '#bbb', 
                        fontWeight: '400' }}>
                          / {total}
                      </span>
                    )}
                  </hgroup>
                </div>
              ))}
            </section>
          </section>

          {/* Desempenho */}
          <section style={{ 
            border: '1px solid #f0f0f0', borderRadius: '8px',
            backgroundColor: 'rgba(255, 255, 255)', height: 'fit-content' }}>

            <div style={{ 
              backgroundColor: "rgba(240, 240, 240, 0.9)", borderBottom: '1px solid #ebebeb', 
              display: 'flex', alignItems: 'center', gap: 8, paddingBlock: '0.4rem' }}>
              <span style={{ width: 3, height: 18, backgroundColor: '#E8773A', borderRadius: 3, flexShrink: 0, display: 'inline-block' }} />
              <p style={{ fontSize: '0.625rem', fontWeight: '700', color: '#333333' }}>
                DESEMPENHO
              </p>
            </div>

            <div style={{ 
              display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', marginBlockEnd: '0.8rem',
              gap: 6, padding: '10px 14px 6px', backgroundColor: "#fff" }}>
              {performanceInfo.map(item => (
                <div
                  key={item.label}
                  style={{
                    backgroundColor: item.highlightBackground ? '#ed701d1a': '#f0f0f04d',
                    border: `1px solid ${item.highlightBorder ? '#E8773A' : '#efefef'}`,
                    borderRadius: '6px',
                    padding: '6px 8px',
                  }}>
                  <div style={{ 
                    display: 'flex', alignItems: 'center', gap: 3, marginBottom: "0.4rem", 
                    color: item.highlightIcon ? '#E8773A' : '#bbb' }}>
                    {item.icon}

                    <span style={{ 
                      fontSize: '0.563rem', fontWeight: '600',
                      color: item.highlightLabel ?  '#E8773A' : '#aaa' }}>
                      {item.label}
                      </span>
                  </div>

                  <div style={{ 
                    fontSize: '1rem', fontWeight: '700', 
                    color: item.highlightValue ? '#E8773A' : '#333333' }}>
                      {item.value}
                    </div>
                </div>
              ))}
            </div>

            {/* Lucro líquido */}
            <div style={{ padding: '0 14px 10px' }}>
              <div style={{ 
                backgroundColor: '#fff', border: '1px solid #efefef',
                borderRadius: '6px', padding: '7px 10px', display: 'flex', alignItems: 'center', gap: 8 }}>
                <span style={{ fontSize: '0.688rem', fontWeight: '700', color: '#aaa', letterSpacing: '0.04em', whiteSpace: 'nowrap' }}>
                  Lucro Líquido
                </span>

                <div style={{ flex: 1, height: 6, backgroundColor: '#e8e8e8', borderRadius: 3 }}>
                  <div
                    style={{
                      height: '100%',
                      borderRadius: 3,
                      transition: 'width 0.3s',
                      width: netShare != null ? `${Math.min(Math.abs(netShare) * 100, 100)}%` : '0%',
                      backgroundColor: '#16a34a',
                    }}/>
                </div>

                <span style={{ fontSize: '0.875rem', fontWeight: '700', color: '#16a34a', whiteSpace: 'nowrap' }}>
                  {netShare != null ? percentFmt.format(netShare) : '—'}
                </span>
              </div>
            </div>         
          </section>

        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: '1.6rem' }}>
          {/* Venda por cor */}
          <section style={{ 
            border: '1px solid #f0f0f0', borderRadius: '8px',
            backgroundColor: 'rgba(255, 255, 255)', height: 'fit-content' }}>
            <hgroup style={{ 
              backgroundColor: "rgba(240, 240, 240, 0.9)", borderBottom: '1px solid #ebebeb', 
              display: 'flex', alignItems: 'center', gap: 8, paddingBlock: '0.4rem' }}>
              <span style={{ width: 3, height: 18, backgroundColor: '#E8773A', borderRadius: 3, flexShrink: 0, display: 'inline-block' }} />
              <p style={{ fontSize: '0.625rem', fontWeight: '700', color: '#333333' }}>
                VENDA POR COR
              </p>
            </hgroup>

            <div style={{ backgroundColor: "#fff", padding: "0.6rem" }}>
              {(colorData.length > 0 ? colorData : [{ label: '—', pct: 0, hex: '#ccc' }]).map(item => (
                <div key={item.label} style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                  <div style={{ width: 10, height: 10, borderRadius: '50%', backgroundColor: item.hex, flexShrink: 0 }} />

                  <span style={{ fontSize: '0.688rem', color: '#555', minWidth: 70 }}>
                    {item.label}
                  </span>

                  <div style={{ flex: 1, height: 6, backgroundColor: '#f0f0f0', borderRadius: 3 }}>
                    <div style={{ height: '100%', width: `${item.pct}%`, backgroundColor: item.hex, borderRadius: 3 }} />
                  </div>
                  <span style={{ fontSize: '0.75rem', color: '#333333', fontWeight: '600', minWidth: 32, textAlign: 'right' }}>
                    {item.pct}%
                  </span>
                </div>
              ))}
            </div>
          </section>

          {/* Venda por grade */}
          <section style={{ 
            border: '1px solid #f0f0f0', borderRadius: '8px', 
            backgroundColor: 'rgba(255, 255, 255)', height: 'fit-content' }}>
            <hgroup style={{ 
              backgroundColor: "rgba(240, 240, 240, 0.9)", borderBottom: '1px solid #ebebeb', 
              display: 'flex', alignItems: 'center', gap: 8, paddingBlock: '0.4rem' }}>
              <span style={{ width: 3, height: 18, backgroundColor: '#E8773A', borderRadius: 3, flexShrink: 0, display: 'inline-block' }} />
              <p style={{ fontSize: '0.625rem', fontWeight: '700', color: '#333333' }}>
                VENDA POR GRADE
              </p>
            </hgroup>

            <div style={{ backgroundColor: "#fff", padding: '10px 14px' }}>
              <div style={{ 
                display: 'flex', height: 24, borderRadius: 12, 
                overflow: 'hidden', backgroundColor: '#f0f0f0' }}>
                {(sizeData.length > 0 ? sizeData : [{ label: '—', pct: 0 }]).map((item, i) => (
                  <div
                    key={item.label}
                    style={{
                      width: `${item.pct}%`,
                      backgroundColor: SIZE_PALETTE[i % SIZE_PALETTE.length],
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      fontSize: '0.688rem', fontWeight: '700', color: '#fff',
                    }}
                  >
                    {item.pct}%
                  </div>
                ))}
              </div>
              
              <div style={{ display: 'flex', gap: 12, marginTop: 6, flexWrap: 'wrap' }}>
                {(sizeData.length > 0 ? sizeData : [{ label: '—', pct: 0 }]).map((item, i) => (
                  <div key={`${item.label}-${item.pct}`} style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                    <div style={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: SIZE_PALETTE[i % SIZE_PALETTE.length] }} />
                    <span style={{ fontSize: '0.688rem', color: '#888' }}>
                      {textFormatter(item.label)}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </section>
        </div>
      </section>
    </div>
  );
};